/**
 * WYSIWYG helpers: convert between stored markup and contenteditable HTML.
 */

const INLINE_MARKUP_REGEX =
  /(\{\{f:([^}|]+)\|(\d+)\}\}([\s\S]*?)\{\{\/f\}\}|\{\{c:(#[0-9a-fA-F]{3,8})\}\}([\s\S]*?)\{\{\/c\}\}|\{\{h:(#[0-9a-fA-F]{3,8})\}\}([\s\S]*?)\{\{\/h\}\}|\{\{a:(left|center|right)\}\}([\s\S]*?)\{\{\/a\}\}|\*\*(.+?)\*\*|__(.+?)__|\*(.+?)\*|~~(.+?)~~|\[([^\]]+)\]\(([^)]+)\))/g;

function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function parseInlineMarkupToHtml(str) {
  if (!str) return "";

  let result = "";
  let lastIndex = 0;
  let match;
  const regex = new RegExp(INLINE_MARKUP_REGEX.source, "g");

  while ((match = regex.exec(str)) !== null) {
    if (match.index > lastIndex) {
      result += escapeHtml(str.slice(lastIndex, match.index));
    }

    if (match[2] && match[3] && match[4] != null) {
      result += `<span style="font-family:${match[2]};font-size:${match[3]}px" data-fc-font="${match[2]}" data-fc-size="${match[3]}">${parseInlineMarkupToHtml(match[4])}</span>`;
    } else if (match[5] && match[6] != null) {
      result += `<span style="color:${match[5]}" data-fc-color="${match[5]}">${parseInlineMarkupToHtml(match[6])}</span>`;
    } else if (match[7] && match[8] != null) {
      result += `<span style="background-color:${match[7]};border-radius:2px;padding:0 2px" data-fc-highlight="${match[7]}">${parseInlineMarkupToHtml(match[8])}</span>`;
    } else if (match[9] && match[10] != null) {
      result += `<span style="display:block;text-align:${match[9]}" data-fc-align="${match[9]}">${parseInlineMarkupToHtml(match[10])}</span>`;
    } else if (match[11]) {
      result += `<strong>${parseInlineMarkupToHtml(match[11])}</strong>`;
    } else if (match[12]) {
      result += `<u>${parseInlineMarkupToHtml(match[12])}</u>`;
    } else if (match[13]) {
      result += `<em>${parseInlineMarkupToHtml(match[13])}</em>`;
    } else if (match[14]) {
      result += `<s>${parseInlineMarkupToHtml(match[14])}</s>`;
    } else if (match[15] && match[16]) {
      result += `<a href="${escapeHtml(match[16])}" target="_blank" rel="noopener noreferrer">${parseInlineMarkupToHtml(match[15])}</a>`;
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < str.length) {
    result += escapeHtml(str.slice(lastIndex));
  }

  return result;
}

export function markupToHtml(markup) {
  if (!markup) return "";

  const lines = markup.split("\n");
  const parts = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const content = line.replace(/^\s+/, "");
    const indentMatch = line.match(/^(\s+)/);
    const indentPx = indentMatch
      ? Math.floor(indentMatch[1].replace(/\t/g, "  ").length / 2) * 16
      : 0;
    const indentStyle = indentPx ? ` style="padding-left:${indentPx}px"` : "";

    const bulletMatch = content.match(/^[-*]\s+(.*)/);
    if (bulletMatch) {
      const items = [];
      while (i < lines.length) {
        const itemContent = lines[i].replace(/^\s+/, "");
        const itemBullet = itemContent.match(/^[-*]\s+(.*)/);
        if (!itemBullet) break;
        items.push(`<li>${parseInlineMarkupToHtml(itemBullet[1]) || "<br>"}</li>`);
        i += 1;
      }
      parts.push(`<ul${indentStyle}>${items.join("")}</ul>`);
      continue;
    }

    const orderedMatch = content.match(/^\d+\.\s+(.*)/);
    if (orderedMatch) {
      const items = [];
      while (i < lines.length) {
        const itemContent = lines[i].replace(/^\s+/, "");
        const itemOrdered = itemContent.match(/^\d+\.\s+(.*)/);
        if (!itemOrdered) break;
        items.push(`<li>${parseInlineMarkupToHtml(itemOrdered[1]) || "<br>"}</li>`);
        i += 1;
      }
      parts.push(`<ol${indentStyle}>${items.join("")}</ol>`);
      continue;
    }

    if (content === "") {
      parts.push("<div><br></div>");
    } else {
      const blockMarkers = unwrapBlockMarkers(content);
      if (blockMarkers.lineHeight || blockMarkers.align) {
        parts.push(
          buildBlockDivHtml({
            lineHeight: blockMarkers.lineHeight,
            align: blockMarkers.align,
            content: blockMarkers.content,
            indentStyle,
          })
        );
      } else {
        parts.push(`<div${indentStyle}>${parseInlineMarkupToHtml(content)}</div>`);
      }
    }
    i += 1;
  }

  return parts.join("");
}

export function rgbToHex(color) {
  if (!color) return null;
  const trimmed = color.trim();
  if (trimmed.startsWith("#")) return trimmed.toLowerCase();
  const match = trimmed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!match) return null;
  return (
    "#" +
    [match[1], match[2], match[3]]
      .map((n) => Number(n).toString(16).padStart(2, "0"))
      .join("")
  );
}

function cleanFontFamily(fontFamily = "") {
  return fontFamily.replace(/['"]/g, "").split(",")[0].trim();
}

function parseFontSize(fontSize = "") {
  const match = String(fontSize).match(/([\d.]+)/);
  return match ? Math.round(Number(match[1])) : null;
}

function serializeInlineNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || "";
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  const tag = node.tagName.toLowerCase();
  const inner = Array.from(node.childNodes).map(serializeInlineNode).join("");

  if (tag === "strong" || tag === "b") return `**${inner}**`;
  if (tag === "em" || tag === "i") return `*${inner}*`;
  if (tag === "u") return `__${inner}__`;
  if (tag === "s" || tag === "strike" || tag === "del") return `~~${inner}~~`;
  if (tag === "a") {
    const href = node.getAttribute("href") || "";
    return `[${inner}](${href})`;
  }
  // A <br> is a soft line break inside its block — dropping it here is what
  // used to swallow Shift+Enter (and Android's Enter) before sending.
  if (tag === "br") return "\n";

  if (tag === "span" || tag === "font") {
    const fcFont = node.getAttribute("data-fc-font");
    const fcSize = node.getAttribute("data-fc-size");
    const fcColor = node.getAttribute("data-fc-color");
    const fcHighlight = node.getAttribute("data-fc-highlight");
    const fcAlign = node.getAttribute("data-fc-align");

    if (fcAlign === "center" || fcAlign === "right") {
      return `{{a:${fcAlign}}}${inner}{{/a}}`;
    }
    if (fcFont && fcSize) {
      return `{{f:${fcFont}|${fcSize}}}${inner}{{/f}}`;
    }
    if (fcColor) {
      return `{{c:${fcColor}}}${inner}{{/c}}`;
    }
    if (fcHighlight) {
      return `{{h:${fcHighlight}}}${inner}{{/h}}`;
    }

    const style = node.style || {};
    const fontFamily = cleanFontFamily(style.fontFamily || node.getAttribute("face") || "");
    const fontSize = parseFontSize(style.fontSize);
    const color = rgbToHex(style.color);
    const highlight = rgbToHex(style.backgroundColor);
    const textAlign = (style.textAlign || "").toLowerCase();

    if (textAlign === "center" || textAlign === "right") {
      return `{{a:${textAlign}}}${inner}{{/a}}`;
    }
    if (fontFamily && fontSize) {
      return `{{f:${fontFamily}|${fontSize}}}${inner}{{/f}}`;
    }
    if (color && color !== "#000000") {
      return `{{c:${color}}}${inner}{{/c}}`;
    }
    if (highlight && highlight !== "#ffffff" && highlight !== "#000000") {
      return `{{h:${highlight}}}${inner}{{/h}}`;
    }
    return inner;
  }

  return inner;
}

function blockIndentFromStyle(style = "") {
  const match = style.match(/padding-left:\s*(\d+)px/i);
  if (!match) return "";
  const spaces = Math.max(1, Math.round(Number(match[1]) / 16));
  return "  ".repeat(spaces);
}

function normalizeLineHeight(value = "") {
  const match = String(value).match(/([\d.]+)/);
  if (!match) return null;
  const num = Number(match[1]);
  if (Number.isNaN(num)) return null;
  return Number.isInteger(num) ? String(num) : String(num);
}

function unwrapBlockMarkers(content) {
  let lineHeight = null;
  let align = null;
  let inner = content;

  const lhMatch = inner.match(/^\{\{lh:([\d.]+)\}\}([\s\S]*)\{\{\/lh\}\}$/);
  if (lhMatch) {
    lineHeight = lhMatch[1];
    inner = lhMatch[2];
  }

  const alignMatch = inner.match(/^\{\{a:(left|center|right)\}\}([\s\S]*)\{\{\/a\}\}$/);
  if (alignMatch) {
    align = alignMatch[1];
    inner = alignMatch[2];
  }

  return { lineHeight, align, content: inner };
}

function buildBlockDivHtml({ lineHeight, align, content, indentStyle = "" }) {
  const styleParts = [];
  if (indentStyle) styleParts.push(indentStyle.replace(/^ style="|"$/g, ""));
  if (lineHeight) styleParts.push(`line-height:${lineHeight}`);
  if (align && align !== "left") styleParts.push(`text-align:${align}`);

  const styleAttr = styleParts.length ? ` style="${styleParts.join(";")}"` : "";
  const dataAttrs = [
    lineHeight ? ` data-fc-line-height="${lineHeight}"` : "",
    align && align !== "left" ? ` data-fc-align="${align}"` : "",
  ].join("");

  return `<div${styleAttr}${dataAttrs}>${parseInlineMarkupToHtml(content)}</div>`;
}

function serializeBlockElement(node) {
  const tag = node.tagName.toLowerCase();

  if (tag === "ul") {
    return Array.from(node.querySelectorAll(":scope > li"))
      .map((li) => `- ${flattenSoftBreaks(serializeInlineNode(li))}`)
      .join("\n");
  }

  if (tag === "ol") {
    return Array.from(node.querySelectorAll(":scope > li"))
      .map((li, index) => `${index + 1}. ${flattenSoftBreaks(serializeInlineNode(li))}`)
      .join("\n");
  }

  if (tag === "li") {
    return flattenSoftBreaks(serializeInlineNode(node));
  }

  if (tag === "div" || tag === "p") {
    const style = node.getAttribute("style") || "";
    const alignAttr = node.getAttribute("data-fc-align");
    const styleAlignMatch = style.match(/text-align:\s*(left|center|right)/i);
    const align = alignAttr || styleAlignMatch?.[1]?.toLowerCase() || null;
    const lineHeightAttr = node.getAttribute("data-fc-line-height");
    const styleLineHeightMatch = style.match(/line-height:\s*([\d.]+)/i);
    const lineHeight = normalizeLineHeight(lineHeightAttr || styleLineHeightMatch?.[1] || "");
    const indent = blockIndentFromStyle(style);

    const bulletPrefix = node.querySelector(":scope > span:first-child");
    const isBulletRow =
      bulletPrefix &&
      bulletPrefix.textContent.trim() === "•" &&
      node.querySelectorAll(":scope > span").length >= 2;

    if (isBulletRow) {
      const textSpan = node.querySelector(":scope > span:nth-child(2)");
      return `${indent}- ${flattenSoftBreaks(serializeInlineNode(textSpan))}`;
    }

    const spans = node.querySelectorAll(":scope > span");
    const orderedMatch = spans[0]?.textContent?.match(/^(\d+)\.$/);
    if (orderedMatch && spans.length >= 2) {
      const textSpan = node.querySelector(":scope > span:nth-child(2)");
      return `${indent}${orderedMatch[1]}. ${flattenSoftBreaks(serializeInlineNode(textSpan))}`;
    }

    const content = serializeInlineNode(node).trim();
    if (!content) return "";

    // Soft breaks split the block into several markup lines; the block's own
    // alignment/spacing markers wrap each line, never span them.
    return content
      .split("\n")
      .map((line) => {
        if (!line.trim()) return line;
        let output = line;
        if (align === "center" || align === "right") {
          output = `{{a:${align}}}${output}{{/a}}`;
        }
        if (lineHeight) {
          output = `{{lh:${lineHeight}}}${output}{{/lh}}`;
        }
        return `${indent}${output}`;
      })
      .join("\n");
  }

  return serializeInlineNode(node).trim();
}

// Markup has no representation for a line break inside a list item.
function flattenSoftBreaks(text) {
  return text.replace(/\n+/g, " ").trim();
}

const BLOCK_TAGS = new Set(["div", "p", "ul", "ol", "li"]);

export function htmlToMarkup(root) {
  if (!root) return "";

  const rootLineHeight = root.getAttribute?.("data-fc-line-height");
  let out = "";
  // Blocks always start on a fresh line; loose inline content keeps building
  // the line already in progress.
  const startNewLine = () => {
    if (out && !out.endsWith("\n")) out += "\n";
  };

  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.replace(/\u00a0/g, " ");
      if (text.trim()) out += text;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.tagName.toLowerCase();

    // A <br> sitting directly in the editor (unwrapped text + Shift+Enter) is
    // one line break, not an empty block.
    if (tag === "br") {
      out += "\n";
      return;
    }

    if (!BLOCK_TAGS.has(tag)) {
      out += serializeInlineNode(node);
      return;
    }

    const block = serializeBlockElement(node);
    if ((tag === "ul" || tag === "ol") && !block) return;
    startNewLine();
    out += `${block}\n`;
  });

  let markup = out.replace(/\n+$/, "").replace(/\n{3,}/g, "\n\n");

  if (rootLineHeight && markup && !markup.includes("{{lh:")) {
    markup = markup
      .split("\n")
      .map((line) => (line.trim() ? `{{lh:${rootLineHeight}}}${line}{{/lh}}` : line))
      .join("\n");
  }

  return markup;
}

export function getEditorPlainText(root) {
  if (!root) return "";
  return root.innerText.replace(/\u00a0/g, " ").replace(/\n$/, "");
}

export function saveEditorSelection() {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  return selection.getRangeAt(0).cloneRange();
}

export function restoreEditorSelection(range) {
  if (!range) return;
  const selection = window.getSelection();
  if (!selection) return;
  selection.removeAllRanges();
  selection.addRange(range);
}

// A saved/current range is only usable if it actually points inside the
// editor. Toolbar buttons keep the page selection where it was, so it can
// easily belong to the chat list or another panel instead.
export function isRangeInsideEditor(editor, range) {
  if (!editor || !range) return false;
  const node = range.commonAncestorContainer;
  return node === editor || editor.contains(node);
}

export function getSelectedText(editor = null) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return "";
  if (editor && !isRangeInsideEditor(editor, selection.getRangeAt(0))) return "";
  return selection.toString();
}

function wrapRangeWithElement(range, element) {
  try {
    range.surroundContents(element);
    return element;
  } catch {
    const fragment = range.extractContents();
    element.appendChild(fragment);
    range.insertNode(element);
    return element;
  }
}

export function wrapSelectionWithSpan(editor, styles = {}, dataset = {}) {
  if (!editor) return;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  if (range.collapsed && !selection.toString()) {
    const span = document.createElement("span");
    Object.entries(styles).forEach(([key, val]) => {
      span.style[key] = val;
    });
    Object.entries(dataset).forEach(([key, val]) => {
      span.setAttribute(key, val);
    });
    span.appendChild(document.createTextNode("text"));
    range.insertNode(span);
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    selection.removeAllRanges();
    selection.addRange(newRange);
    return;
  }

  const span = document.createElement("span");
  Object.entries(styles).forEach(([key, val]) => {
    span.style[key] = val;
  });
  Object.entries(dataset).forEach(([key, val]) => {
    span.setAttribute(key, val);
  });

  wrapRangeWithElement(range, span);
  selection.removeAllRanges();
  const newRange = document.createRange();
  newRange.selectNodeContents(span);
  selection.addRange(newRange);
}

export function applyEditorCommand(editor, command, value = null) {
  if (!editor) return;
  editor.focus();
  document.execCommand(command, false, value);
}

export function isSelectionInsideList(editor) {
  if (!editor) return false;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  let node = selection.anchorNode;
  if (node && node.nodeType === Node.TEXT_NODE) {
    node = node.parentElement;
  }

  while (node && node !== editor) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      if (tag === "ul" || tag === "ol" || tag === "li") return true;
    }
    node = node.parentElement;
  }
  return false;
}

function findAncestorListItem(editor) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return { selection: null, li: null };

  let node = selection.anchorNode;
  if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;

  let li = null;
  while (node && node !== editor) {
    if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === "li") {
      li = node;
      break;
    }
    node = node.parentElement;
  }
  return { selection, li };
}

// Break out of a list at the caret and drop onto a plain, left-aligned line.
// Content from the caret to the end of the current item moves onto the new
// line; items below continue in a fresh list underneath. Returns true if
// handled. Used for Shift+Enter (always) and Enter on an empty item.
export function breakOutOfListAtCaret(editor) {
  if (!editor) return false;
  const { selection, li } = findAncestorListItem(editor);
  if (!selection || !li) return false;

  const list = li.parentElement;
  if (!list || !(list.tagName.toLowerCase() === "ul" || list.tagName.toLowerCase() === "ol")) {
    return false;
  }

  const range = selection.getRangeAt(0);
  if (!range.collapsed) range.deleteContents();

  // Pull everything from the caret to the end of the item onto the new line.
  const tail = document.createRange();
  tail.selectNodeContents(li);
  tail.setStart(range.endContainer, range.endOffset);
  const frag = tail.extractContents();

  const newBlock = document.createElement("div");
  if (frag.textContent.replace(/\u200b/g, "").trim() !== "" || frag.querySelector("img")) {
    newBlock.appendChild(frag);
  } else {
    newBlock.appendChild(document.createElement("br"));
  }

  // Any items after the current one continue in a fresh list below the new line.
  const following = [];
  let sib = li.nextElementSibling;
  while (sib) {
    following.push(sib);
    sib = sib.nextElementSibling;
  }

  list.parentNode.insertBefore(newBlock, list.nextSibling);

  if (following.length) {
    const newList = document.createElement(list.tagName.toLowerCase());
    following.forEach((item) => newList.appendChild(item));
    newBlock.parentNode.insertBefore(newList, newBlock.nextSibling);
  }

  // Drop the now-empty item; drop the list too if nothing is left in it.
  if (li.textContent.replace(/\u200b/g, "").trim() === "" && !li.querySelector("img")) {
    li.remove();
  }
  if (!list.querySelector("li")) list.remove();

  const caret = document.createRange();
  caret.setStart(newBlock, 0);
  caret.collapse(true);
  selection.removeAllRanges();
  selection.addRange(caret);

  return true;
}

// Enter on an empty list item exits to a left-aligned line; a non-empty item
// falls through so the browser creates the next bullet/number natively.
export function exitEmptyListItem(editor) {
  const { li } = findAncestorListItem(editor);
  if (!li) return false;
  if (li.textContent.replace(/\u200b/g, "").trim() !== "") return false;
  return breakOutOfListAtCaret(editor);
}

export function insertTextAtSelection(editor, text) {
  if (!editor) return;
  editor.focus();
  document.execCommand("insertText", false, text);
}

// True when anything renderable follows `node` before the end of the editor.
// Splitting a text node at its end leaves an empty text node behind, which
// must not count as content.
function hasContentAfter(node, root) {
  let current = node;
  while (current && current !== root) {
    let sibling = current.nextSibling;
    while (sibling) {
      if (sibling.nodeType !== Node.TEXT_NODE || sibling.textContent !== "") return true;
      sibling = sibling.nextSibling;
    }
    current = current.parentNode;
  }
  return false;
}

// Insert a soft line break at the caret. Android's keyboard commits its IME
// composition on Enter and then loses the browser's own newline, so on Android
// the composer inserts the break itself instead of relying on the default.
export function insertLineBreakAtCaret(editor) {
  if (!editor) return false;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const range = selection.getRangeAt(0);
  if (!isRangeInsideEditor(editor, range)) return false;

  if (!range.collapsed) range.deleteContents();

  const br = document.createElement("br");
  range.insertNode(br);

  // A <br> that ends the content isn't rendered on its own, so the caret would
  // stay on the old line — a second one gives it somewhere to go.
  if (!hasContentAfter(br, editor)) {
    br.parentNode.insertBefore(document.createElement("br"), br.nextSibling);
  }

  const caret = document.createRange();
  caret.setStartAfter(br);
  caret.collapse(true);
  selection.removeAllRanges();
  selection.addRange(caret);
  return true;
}

export function insertLinkAtSelection(editor, text, url, savedRange = null) {
  if (!editor) return;
  // Focus BEFORE restoring the selection — restoring a range while the editor
  // is unfocused and then focusing it collapses the range, so createLink/insert
  // would run against an empty selection and no link would be created.
  editor.focus();
  // Only restore a range that belongs to the editor. A range left over from a
  // click elsewhere on the page (the chat list, a modal input) would otherwise
  // drop the link outside the composer, where it silently disappears.
  if (savedRange && isRangeInsideEditor(editor, savedRange)) {
    restoreEditorSelection(savedRange);
  }

  const displayText = (text || "").trim() || url.trim();
  const normalizedUrl = /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
  const selection = window.getSelection();
  const range =
    selection && selection.rangeCount > 0 && isRangeInsideEditor(editor, selection.getRangeAt(0))
      ? selection.getRangeAt(0)
      : null;

  if (range && !range.collapsed && selection.toString()) {
    applyEditorCommand(editor, "createLink", normalizedUrl);
    return;
  }

  const link = document.createElement("a");
  link.href = normalizedUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.textContent = displayText;

  if (range) {
    range.insertNode(link);
  } else {
    // No caret inside the editor (the toolbar was clicked without typing
    // first) — drop the link at the end of the content.
    editor.appendChild(link);
  }

  placeCaretAfterNode(link, selection);
}

// Leave the caret just after the inserted link so typing continues as plain
// text instead of extending the anchor.
function placeCaretAfterNode(node, selection) {
  const sel = selection || window.getSelection();
  if (!sel) return;
  const caret = document.createRange();
  caret.setStartAfter(node);
  caret.collapse(true);
  sel.removeAllRanges();
  sel.addRange(caret);
}

export function applyFontAndSizeToEditor(editor, savedRange, fontFamily, fontSize) {
  restoreEditorSelection(savedRange);
  wrapSelectionWithSpan(
    editor,
    { fontFamily, fontSize: `${fontSize}px` },
    { "data-fc-font": fontFamily, "data-fc-size": String(fontSize) }
  );
}

export function applyTextColorToEditor(editor, savedRange, color) {
  restoreEditorSelection(savedRange);
  wrapSelectionWithSpan(editor, { color }, { "data-fc-color": color });
}

export function applyHighlightColorToEditor(editor, savedRange, color) {
  restoreEditorSelection(savedRange);
  wrapSelectionWithSpan(
    editor,
    { backgroundColor: color, borderRadius: "2px", padding: "0 2px" },
    { "data-fc-highlight": color }
  );
}

function isSpacingBlock(node) {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
  const tag = node.tagName.toLowerCase();
  return tag === "div" || tag === "p" || tag === "li";
}

function getBlockAncestor(node, editor) {
  let current = node;
  if (current?.nodeType === Node.TEXT_NODE) {
    current = current.parentElement;
  }

  while (current && current !== editor) {
    if (isSpacingBlock(current)) return current;
    current = current.parentElement;
  }

  return null;
}

function rangeIntersectsNode(range, node) {
  if (typeof range.intersectsNode === "function") {
    return range.intersectsNode(node);
  }

  const nodeRange = document.createRange();
  try {
    nodeRange.selectNodeContents(node);
  } catch {
    return false;
  }

  return (
    range.compareBoundaryPoints(Range.END_TO_START, nodeRange) < 0 &&
    range.compareBoundaryPoints(Range.START_TO_END, nodeRange) > 0
  );
}

function getBlocksInSelection(editor) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return [];

  return getBlocksInRange(editor, selection.getRangeAt(0));
}

function getBlocksInRange(editor, range) {
  if (!editor || !range) return [];

  const blocks = new Set();

  const addBlock = (node) => {
    const block = getBlockAncestor(node, editor);
    if (block) blocks.add(block);
  };

  addBlock(range.startContainer);
  addBlock(range.endContainer);

  editor.querySelectorAll(":scope > div, :scope > p, :scope > ul, :scope > ol").forEach((node) => {
    const tag = node.tagName.toLowerCase();
    if (tag === "ul" || tag === "ol") {
      node.querySelectorAll(":scope > li").forEach((li) => {
        if (rangeIntersectsNode(range, li)) blocks.add(li);
      });
      return;
    }
    if (rangeIntersectsNode(range, node)) blocks.add(node);
  });

  if (blocks.size === 0 && editor.textContent.replace(/\u200b/g, "").trim()) {
    blocks.add(editor);
  }

  return Array.from(blocks);
}

export function applyLineSpacingToEditor(editor, savedRange, lineHeight) {
  if (!editor) return;
  editor.focus();
  if (savedRange) restoreEditorSelection(savedRange);

  const blocks = getBlocksInSelection(editor);
  if (!blocks.length) return;

  blocks.forEach((block) => {
    if (lineHeight) {
      block.style.lineHeight = lineHeight;
      block.setAttribute("data-fc-line-height", lineHeight);
    } else {
      // Clearing puts the block back on the editor's default spacing.
      block.style.removeProperty("line-height");
      block.removeAttribute("data-fc-line-height");
    }
  });
}

// Line spacing of the blocks the caret/selection touches, so the menu can show
// which option is currently in effect. Returns null when the blocks disagree.
export function getLineSpacingAtSelection(editor, savedRange, defaultLineHeight = "1") {
  if (!editor) return defaultLineHeight;

  const selection = window.getSelection();
  const liveRange =
    selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  const isInEditor = (r) => r && editor.contains(r.commonAncestorContainer);
  const range = isInEditor(liveRange)
    ? liveRange
    : isInEditor(savedRange)
      ? savedRange
      : null;
  if (!range) return defaultLineHeight;

  const blocks = getBlocksInRange(editor, range);
  if (!blocks.length) return defaultLineHeight;

  const values = blocks.map((block) => {
    const attr = block.getAttribute?.("data-fc-line-height");
    const styleValue = block.style?.lineHeight;
    return normalizeLineHeight(attr || styleValue || "") || defaultLineHeight;
  });

  return values.every((v) => v === values[0]) ? values[0] : null;
}
